import isEmpty from 'lodash/isEmpty';
import { LocaleCode, LOCALES, MarketCode } from '@tink/config/src/markets';
import { match } from '@formatjs/intl-localematcher';
import { GradualReleaseLabels, TestExposure } from '../../../utilities/useGradualRolloutTypes';
import { DEFAULT_LOCALE, DEFAULT_MARKET } from '../../../config';
import { isEnumType } from '../../../utilities/urlParameters';

export type ReleaseLabelAndTestExposure = Partial<Record<GradualReleaseLabels, TestExposure>>;

export enum TestOrControl {
  TEST = 'TEST',
  CONTROL = 'CONTROL',
}

type GetValidReleaseTogglesParams = {
  queryParam: string | null;
  releaseLabels?: GradualReleaseLabels | Record<string, string>;
};

const replaceUnderscore = (locale: LocaleCode | string): string => {
  return locale.replace('_', '-');
};
const replaceDash = (locale: LocaleCode | string): string => {
  return locale.replace('-', '_');
};

export const getClosestLocale = (localeParameter: string): LocaleCode => {
  const supportedLocales = LOCALES.map((localeCode) => replaceUnderscore(localeCode));

  try {
    return replaceDash(
      match([replaceUnderscore(localeParameter)], supportedLocales, DEFAULT_LOCALE)
    ) as LocaleCode;
  } catch {
    return DEFAULT_LOCALE;
  }
};

export const getValidatedLocale = (locale: LocaleCode | string | undefined | null): LocaleCode => {
  if (!locale) {
    return DEFAULT_LOCALE;
  }

  if (locale && isEnumType(LocaleCode)(locale)) {
    return locale;
  }

  if (locale.match(/[a-z]{2}[_-][a-zA-Z]{2}/)) {
    return getClosestLocale(locale);
  }

  return DEFAULT_LOCALE;
};

export const getValidatedMarket = (market: MarketCode | string | undefined | null): MarketCode =>
  market && isEnumType(MarketCode)(market) ? market : DEFAULT_MARKET;

export const isTrue = (str: string | undefined | null): boolean =>
  Boolean(str && /^true$/.test(str));

export const getValidReleaseToggles = ({
  queryParam,
  releaseLabels = GradualReleaseLabels,
}: GetValidReleaseTogglesParams) => {
  if (!queryParam) {
    return undefined;
  }

  const releaseGroupToggles = queryParam.split(',');

  const releaseToggleRegex = new RegExp(
    `((${Object.values(releaseLabels).join('|')}):(${Object.values(TestOrControl).join('|')}))`
  );

  const validReleaseToggles = releaseGroupToggles.filter((releaseToggle) =>
    releaseToggleRegex.test(releaseToggle)
  );

  const releaseLabelsAndTestExposure = validReleaseToggles.reduce<ReleaseLabelAndTestExposure>(
    (releaseToggles, currentReleaseToggle) => {
      const [releaseLabel, testOrControl] = currentReleaseToggle.split(':');

      const testExposure =
        testOrControl === TestOrControl.TEST
          ? TestExposure.OUTSIDE_TEST_WITH_EXPERIMENTAL_FEATURE
          : TestExposure.OUTSIDE_TEST;

      return { ...releaseToggles, [releaseLabel]: testExposure };
    },
    {}
  );

  const hasValidReleaseToggles = !isEmpty(releaseLabelsAndTestExposure);

  return hasValidReleaseToggles ? releaseLabelsAndTestExposure : undefined;
};
